import { create } from "zustand";
import { toast } from "react-toastify";
import axiosInstance from "../../../../Hooks/axoisInstance";
import { API_BASE_URL1 } from "../../../Services/baseApiUrl";

const RFID_CATEGORY_API = {
  GET_ASSET_TYPES: `${API_BASE_URL1}MasterDetails/GetAssetTypes`,
  GET_ALL_ASSET_CATEGORY: `${API_BASE_URL1}MasterDetails/GetAllAssetCategory`,
  ADD_OR_UPDATE: `${API_BASE_URL1}MasterCreate/AddOrUpdateAssetCategory`,
};

const buildCategoryQueryString = (queryParams = {}) => {
  const params = new URLSearchParams();

  if (queryParams.IsActive !== undefined && queryParams.IsActive !== "") {
    params.append("IsActive", queryParams.IsActive);
  }

  if (
    queryParams.AssetCategoryFilter !== undefined &&
    queryParams.AssetCategoryFilter !== ""
  ) {
    params.append("AssetCategoryFilter", queryParams.AssetCategoryFilter);
  }

  return params.toString();
};

const getErrorMessage = (data, fallback) => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

const getAssetTypeName = (assetTypeId, assetTypes = []) =>
  assetTypes.find((type) => Number(type.assetTypeId) === Number(assetTypeId))
    ?.assetTypeName || "Unknown";

const mapRfidCategoryData = (categoryList, assetTypes = []) =>
  categoryList.map((category) => {
    const assetTypeId = category.assetTypeId ?? category.rfidTypeId;
    const assetCategoryId = category.assetCategoryId ?? category.rfidCategoryId;
    const assetCategoryName =
      category.assetCategoryName ?? category.categoryName ?? "";
    const assetTypeName =
      category.assetTypeName ||
      category.rfidTypeName ||
      getAssetTypeName(assetTypeId, assetTypes);

    return {
      ...category,
      rfidCategoryId: assetCategoryId,
      rfidTypeId: assetTypeId,
      rfidTypeName: assetTypeName,
      categoryName: assetCategoryName,
      assetCategoryId,
      assetCategoryName,
      assetTypeId,
      assetTypeName,
      isActive: Boolean(category.isActive),
      status: category.isActive ? "Active" : "InActive",
    };
  });

export const useRfidCategoryStore = create((set, get) => ({
  rfidCategories: [],
  filteredRfidCategories: [],
  assetTypes: [],
  isLoading: false,
  isSaving: false,
  isTypesLoading: false,
  error: "",
  filters: {
    searchTerm: "",
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  applyFilters: (data, currentFilters) => {
    const filters = currentFilters ?? get().filters;
    let result = [...(data ?? get().rfidCategories)];

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.categoryName?.toLowerCase().includes(term) ||
          item.rfidTypeName?.toLowerCase().includes(term) ||
          item.assetCategoryName?.toLowerCase().includes(term) ||
          item.assetTypeName?.toLowerCase().includes(term)
      );
    }

    set({ filteredRfidCategories: result });
    return result;
  },

  fetchAssetTypes: async () => {
    set({ isTypesLoading: true });
    try {
      const response = await axiosInstance.get(RFID_CATEGORY_API.GET_ASSET_TYPES);
      const typeList = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      set({ assetTypes: typeList });
      return { success: true, data: typeList };
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load asset types."
      );
      set({ assetTypes: [] });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isTypesLoading: false });
    }
  },

  fetchRfidCategories: async (queryParams = {}) => {
    set({ isLoading: true, error: "", rfidCategories: [] });
    try {
      if (!get().assetTypes.length) {
        await get().fetchAssetTypes();
      }

      const queryString = buildCategoryQueryString(queryParams);
      const url = queryString
        ? `${RFID_CATEGORY_API.GET_ALL_ASSET_CATEGORY}?${queryString}`
        : RFID_CATEGORY_API.GET_ALL_ASSET_CATEGORY;

      const response = await axiosInstance.get(url);
      const categoryList = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      const mappedData = mapRfidCategoryData(
        categoryList,
        get().assetTypes
      ).sort((a, b) => {
        if (a.isActive === b.isActive) return 0;
        return a.isActive ? -1 : 1;
      });
      set({ rfidCategories: mappedData });
      get().applyFilters(mappedData, get().filters);
      return { success: true, data: mappedData };
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load asset category data."
      );
      set({ rfidCategories: [], filteredRfidCategories: [], error: message });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isLoading: false });
    }
  },

  saveRfidCategory: async (payload, isEdit = false) => {
    set({ isSaving: true, error: "" });
    try {
      const requestPayload = {
        assetCategoryId: isEdit ? Number(payload.rfidCategoryId) : 0,
        assetCategoryName: payload.categoryName,
        assetTypeId: Number(payload.rfidTypeId),
        isActive: Boolean(payload.isActive),
      };

      const response = await axiosInstance.post(
        RFID_CATEGORY_API.ADD_OR_UPDATE,
        requestPayload
      );

      const message = getErrorMessage(
        response?.data,
        isEdit
          ? "Asset category updated successfully."
          : "Asset category created successfully."
      );
      toast.success(message);
      await get().fetchRfidCategories();
      return { success: true, data: response?.data };
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Unable to save asset category details."
      );
      set({ error: message });
      toast.error(message);
      return { success: false, message };
    } finally {
      set({ isSaving: false });
    }
  },
}));
